import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { isLiveState } from '@avslack/shared';
import { useHuddle } from '../huddle/HuddleProvider';
import { useServiceSettings } from '../data/service';
import { declineHuddle, dialPhone } from '../lib/api';
import { useCallDevice } from '../lib/call-device';
import { hasPendingDial, takePendingDial } from '../lib/pending-dial';
import { setServiceCallHandler, setServiceView } from '../lib/service-call';
import {
  closeServicePip,
  openServicePip,
  pipIsSupported,
  usePipContainer,
} from '../lib/service-pip';
import { HuddleRoom } from './HuddleRoom';
import { ServicePanel } from './ServicePanel';

/**
 * A VEVOSZOLGALATI MOD KEZELOJE a fo ablakban (docs/20-vevoszolgalat.html).
 *
 * Ha a felhasznalo vevoszolgalati modja be van kapcsolva (a harom szint feloldott
 * erteke):
 *
 *  - az ELSO kattintasra / billentyure megnyitja a lebego panelt (a bongeszo
 *    kattintas nelkul nem engedi) - vagy egy allando jelzes kinal ra;
 *  - amig a panel nyitva van, a hivas a FO ABLAKBAN fut (a panel ugy fer hozza a
 *    hivas allapotahoz), es a csorgest a panel mutatja: nincs kulon felugro ablak,
 *    ami elvenne a fokuszt a tavoli asztaltol;
 *  - a panelen a hivasfunkciok: nemitas, atadas, telefon bontasa, ugyfelkartya,
 *    hivasjegyzet, kilepes; a teljes hivasfelulet a fo ablakban nyithato.
 *
 * Ha a panel nincs nyitva, semmit nem csinal: a megszokott ut (kulon ablak,
 * rendszer-ertesites) marad.
 */
export function ServiceHost({ meId }: { meId: string }) {
  const { data: service } = useServiceSettings();
  const enabled = service?.effective.enabled ?? false;
  const autoOpen = service?.effective.autoOpen ?? true;
  const supported = pipIsSupported();
  const container = usePipContainer();

  const { huddle, state, join, isMuted, toggleMute, leave } = useHuddle();
  const live = isLiveState(state);

  /** A csorgo hivas csatornaja (a panel mutatja). */
  const [ringChannelId, setRingChannelId] = useState<string | null>(null);
  /** Teljes hivasfelulet a fo ablakban. */
  const [fullView, setFullView] = useState(false);
  /** A kovetkezo kattintas megnyissa-e a panelt. */
  const [armed, setArmed] = useState(true);

  // A kezelo a legfrissebb allapotot lassa, anelkul, hogy minden valtozasnal ujra regisztralnank.
  const latest = useRef({ live, channelId: huddle?.channelId ?? null, join });
  latest.current = { live, channelId: huddle?.channelId ?? null, join };

  // ─── A mod kikapcsolasa: a panel is bezarul ────────────────────────────────
  useEffect(() => {
    if (!enabled && container) closeServicePip();
  }, [enabled, container]);

  // ─── Elso kattintasra / billentyure megnyilik ──────────────────────────────
  useEffect(() => {
    if (!enabled || !autoOpen || !supported || container || !armed) return;

    const open = () => {
      // Egyszeri: sikertelen megnyitasnal (pl. szabalyzat) ujra probalkozhat a kovetkezo.
      void openServicePip().then((win) => {
        if (!win) setArmed(true);
      });
      setArmed(false);
    };
    window.addEventListener('pointerdown', open, { once: true, capture: true });
    window.addEventListener('keydown', open, { once: true, capture: true });
    return () => {
      window.removeEventListener('pointerdown', open, { capture: true });
      window.removeEventListener('keydown', open, { capture: true });
    };
  }, [enabled, autoOpen, supported, container, armed]);

  // ─── Hivas-iranyitas: amig a panel nyitva van ──────────────────────────────
  useEffect(() => {
    if (!enabled || !container) {
      setServiceCallHandler(null);
      return;
    }
    setServiceCallHandler(({ channelId, incoming }) => {
      const current = latest.current;
      if (incoming) {
        // Mar hivasban vagyunk: a masodik hivast a megszokott ut kezeli (a felhasznalo donti el).
        if (current.live) return false;
        setRingChannelId(channelId);
        return true;
      }
      if (current.live) {
        // Ugyanaz a hivas: csak elohozzuk. Mas hivas: a megszokott ut.
        if (current.channelId !== channelId) return false;
        setFullView(true);
        return true;
      }
      // Sajat inditas (pl. a csatorna fejlecebol): belepunk a fo ablakban, teljes felulettel.
      void current.join(
        channelId,
        undefined,
        hasPendingDial(channelId) ? { quiet: true } : undefined,
      );
      setFullView(true);
      return true;
    });
    return () => setServiceCallHandler(null);
  }, [enabled, container]);

  // A teljes felulet "bezarasa" (pl. a hivas vegen) csak visszacsukja - nem zarja az ablakot.
  useEffect(() => {
    setServiceView(fullView ? () => setFullView(false) : null);
    return () => setServiceView(null);
  }, [fullView]);

  // A hivas elinditasa utan a csorgest elengedjuk; a vege utan a teljes felulet bezarul.
  useEffect(() => {
    if (live) setRingChannelId(null);
  }, [live]);
  useEffect(() => {
    if (!live && (state === 'idle' || state === 'failed')) setFullView(false);
  }, [live, state]);

  // ─── Visszahivas: a fo ablak felirta a szamot, belepes utan tarcsazunk ─────
  useEffect(() => {
    if (state !== 'connected' || !huddle) return;
    const pending = takePendingDial(huddle.channelId);
    if (!pending) return;
    void dialPhone(huddle.id, pending.number, pending.lineId).catch(() => undefined);
  }, [state, huddle]);

  // ─── A hivas "telefon-resze": ebren tartas, rendszer-vezerlok ──────────────
  useCallDevice({
    active: enabled && Boolean(container) && live,
    title: 'Hívás folyamatban',
    muted: isMuted,
    onToggleMute: toggleMute,
    onHangup: leave,
  });

  const accept = useCallback(
    (channelId: string) => {
      setRingChannelId(null);
      // FELVETEL: ha kozben egy kolléga felvette, a szerver elutasitja.
      void join(channelId, undefined, { answer: true });
    },
    [join],
  );
  const decline = useCallback((channelId: string) => {
    setRingChannelId(null);
    // Szolunk a szervernek: kulonben a hivo vegtelenul azt latna, hogy "hivjuk".
    void declineHuddle(channelId).catch(() => undefined);
  }, []);

  if (!enabled || !supported) return null;

  return (
    <>
      {/* A panel a lebego ablakba rajzolodik; ugyanazt a kontextust latja, mint a fo oldal. */}
      {container &&
        createPortal(
          <ServicePanel
            meId={meId}
            ringChannelId={ringChannelId}
            onAccept={accept}
            onDecline={decline}
            onRingDone={() => setRingChannelId(null)}
            onFullView={() => setFullView(true)}
            onFocusMain={() => window.focus()}
          />,
          container,
        )}

      {/* Allando jelzes, ha a panel nincs nyitva: a hivas ilyenkor NEM a panelben csorog. */}
      {!container && (
        <div className="fixed left-1/2 top-2 z-[60] -translate-x-1/2">
          <button
            type="button"
            onClick={() => {
              void openServicePip().then((win) => {
                if (win) setArmed(false);
              });
            }}
            className="flex items-center gap-2 rounded-pill border border-border bg-surface-raised px-3.5 py-1.5 text-xs font-medium shadow-(--shadow-lift) hover:bg-surface-hover"
          >
            <span aria-hidden className="size-2 rounded-full bg-[var(--danger)]" />
            Vevőszolgálati panel nincs bekapcsolva
            <span className="font-semibold text-accent">Megnyitás</span>
          </button>
        </div>
      )}

      {/*
        Teljes hivasfelulet a fo ablakban (nem kulon ablak, hogy a panel ne veszitse el a hivast).

        A reteg a hivassav (z-40) FOLOTT, de a parbeszedablakok es menuk (z-50) ALATT
        van: ezek a body-ba portaloznak, es egy magasabb reteg ala nyilnanak - a
        Szavazas, Kiscsoportok, Vendeg gomb latszolag nem csinalna semmit.
      */}
      {fullView && state !== 'idle' && (
        <div className="fixed inset-0 z-[45] bg-surface">
          {/* Elo hivasban a fejlec gombsoraban van; csatlakozas kozben nincs fejlec. */}
          {(state === 'preflight' || state === 'connecting') && (
            <button
              type="button"
              onClick={() => setFullView(false)}
              className="absolute right-3 top-2.5 z-10 rounded-pill border border-border-soft bg-surface-raised px-3 py-1 text-xs font-semibold text-ink-muted hover:text-ink"
            >
              Kicsinyítés
            </button>
          )}
          <HuddleRoom meId={meId} onMinimize={() => setFullView(false)} />
        </div>
      )}
    </>
  );
}
